import { Context, useContext } from 'react';

import type { CustomTestCreateWizardContextValue, CustomTestEditContextValue } from 'pages/custom_tests_v1';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';

// Categories that carry both “Live in the App” (in-app) and “Browser Based” question
// sets. For these the delivery mode selects which question bank the Questions step pulls,
// so it must be chosen explicitly (mirrors the V2 wizard behaviour).
const HYBRID_CATEGORY_IDS = [21, 40];

type CustomTestSettingsProps = {
    context: Context<CustomTestCreateWizardContextValue> | Context<CustomTestEditContextValue>;
    showRandomize?: boolean;
};

export const CustomTestSettings = (props: CustomTestSettingsProps) => {

    const { context, showRandomize = true } = props;

    const { formatMessage } = useIntl();
    const { name, categoryId = 0, languageId = 0, languages = [], minScore, minutes, modeId, nameValidationError = '', randomly,
        setProviderState, totalQuestions = 0, useRandomly } = useContext(context);

    const isHybridCategory = HYBRID_CATEGORY_IDS.includes(categoryId);

    const handleLanguageChange = e => setProviderState({ languageId: parseInt(e.target.value, 10) });

    const handleMinScoreChange = e => setProviderState({ minScore: parseInt(e.target.value, 10) || 0 });

    const handleMinutesChange = e => setProviderState({ minutes: parseInt(e.target.value, 10) || 0 });

    const handleModeChange = e => setProviderState({ modeId: e.target.checked ? 1 : 2 });

    const handleTestTypeChange = e => setProviderState({ modeId: parseInt(e.target.value, 10) });

    const handleNameChange = e => setProviderState({ name: e.target.value });

    const handleRandomlyChange = e => setProviderState({ randomly: parseInt(e.target.value, 10) || 0 });

    const handleUseRandomlyChange = e => setProviderState({ useRandomly: e.target.checked });

    return (
        <div>
            <Row>
                <Col className="mb-3" md={6} sm={12}>
                    <GMetrixInput
                        required
                        label={formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' })}
                        onChange={handleNameChange}
                        validType={nameValidationError ? 'invalid' : undefined}
                        value={name}
                    />
                    {nameValidationError && (
                    <Form.Text className="text-danger">
                        {nameValidationError}
                    </Form.Text>
                    )}
                </Col>
            </Row>

            {languages.length > 0 && (
            <Row>
                <Col className="mb-3" md={4} sm={12}>
                    <Form.Label className={'fw-bold'}>
                        {<FormattedMessage defaultMessage="Language" id="language.bc205d83" />}
                    </Form.Label>
                    <Form.Select onChange={handleLanguageChange} value={languageId}>
                        {languages.map(lang => (
                            <option key={lang.Id} value={lang.Id}>
                                {lang.Name}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>
            )}

            <Row>
                <Col className="mb-3" md={3} sm={6}>
                    <GMetrixInput
                        required
                        label={formatMessage({ id: 'minimum_score_percent.9c3d3125', defaultMessage: 'Minimum Score (%)' })}
                        max={100}
                        min={1}
                        onChange={handleMinScoreChange}
                        type="number"
                        value={minScore.toString()}
                    />
                </Col>
                <Col className="mb-3" md={3} sm={6}>
                    <GMetrixInput
                        required
                        label={formatMessage({ id: 'time_limit_minutes.cd2ccbb5', defaultMessage: 'Time Limit (minutes)' })}
                        max={600}
                        min={1}
                        onChange={handleMinutesChange}
                        type="number"
                        value={minutes.toString()}
                    />
                </Col>
            </Row>

            <Row>
                <Col className="mb-3" md={6} sm={12}>
                    {/* 'Both' (3) is a Custom Tests 2.0 linked mode pair the V1 controls cannot
                        represent — render a read-only notice and leave modeId untouched so Save
                        posts 3 and the server keeps the pair intact. Compared via Number()
                        because V1's EMode enums carry no Both member. */}
                    {Number(modeId) === 3 ?
                        (
                            <>
                                <div className="d-flex align-items-center gap-2 text-muted">
                                    <i className="fa-solid fa-lock" />
                                    <span>
                                        <FormattedMessage defaultMessage="Both modes (Testing & Training)" id="both_modes_testing_and_training.0565ccdf" />
                                    </span>
                                </div>
                                <Form.Text className="text-muted">
                                    <FormattedMessage defaultMessage="Mode is managed in the new Custom Tests experience." id="mode_is_managed_in_the_new_custom_tests.78b3ce01" />
                                </Form.Text>
                            </>
                        )
                        : isHybridCategory ?
                            (
                                <>
                                    <Form.Label className={'fw-bold d-inline-flex align-items-center gap-1'}>
                                        <span>
                                            <FormattedMessage defaultMessage="Test Type" id="test_type.30d1f5ef" />
                                        </span>
                                        <OverlayTrigger
                                            overlay={(
                                                <Tooltip>
                                                    <FormattedMessage defaultMessage="Training mode disables the time limit and enables help text. Testing mode is timed with no help text." id="training_mode_disables_the_time_limit_an.1f6c7a7b" />
                                                </Tooltip>
                                            )}
                                            placement={'top'}
                                        >
                                            <i className={'fa-light fa-circle-info text-muted'} />
                                        </OverlayTrigger>
                                    </Form.Label>
                                    <Form.Select onChange={handleTestTypeChange} value={modeId}>
                                        {/* <option> only renders string children; a React element shows "[object Object]" */}
                                        <option value={1}>
                                            {formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' })}
                                        </option>
                                        <option value={2}>
                                            {formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' })}
                                        </option>
                                    </Form.Select>
                                </>
                            )
                            :
                            (
                                <>
                                    <GMetrixCheckInput
                                        checked={modeId === 1}
                                        label={formatMessage({ id: 'training_mode.26ca842d', defaultMessage: 'Training Mode' })}
                                        onChange={handleModeChange}
                                        type="checkbox"
                                    />
                                    <Form.Text className="text-muted">
                                        <FormattedMessage defaultMessage="This will disable the time limit and enable help text." id="this_will_disable_the_time_limit_and_ena.8d70659b" />
                                    </Form.Text>
                                </>
                            )}
                </Col>
            </Row>

            {showRandomize && (
            <Row>
                <Col className="mb-3" md={6} sm={12}>
                    <GMetrixCheckInput
                        checked={useRandomly}
                        label={formatMessage({ id: 'randomize_questions.26374601', defaultMessage: 'Randomize Questions' })}
                        onChange={handleUseRandomlyChange}
                        type="checkbox"
                    />

                    {useRandomly && (
                        <div className="mt-2">
                            <GMetrixInput
                                label={formatMessage({ id: 'number_of_random_questions_to_show.cdd6ff9a', defaultMessage: 'Number of random questions to show' })}
                                max={totalQuestions > 0 ? totalQuestions : undefined}
                                min={1}
                                onChange={handleRandomlyChange}
                                type="number"
                                value={randomly.toString()}
                            />
                            {totalQuestions > 0 && (
                            <Form.Text className="text-muted">
                                {`${formatMessage({ id: 'total_questions_selected.bcbc4913', defaultMessage: 'Total questions selected' })}: ${totalQuestions}`}
                            </Form.Text>
                            )}
                        </div>
                    )}

                </Col>
            </Row>
            )}
        </div>
    );

};
